<template>
  <div>
    <v-card-text style="padding: 0 16px;">
      <v-layout row wrap align-center>
        <v-spacer />
        <v-flex xs6 sm4 md3>
          <v-select
            :items="fields.filter(g => g.value !== 'students' || perm !== 'student')"
            v-model="heads"
            multiple
            dense
          >
            <template slot="selection" slot-scope="{ index }">
              <span
                v-if="index === 0"
                class="grey--text caption"
              >{{ headers.length }} fields</span>
            </template>
          </v-select>
        </v-flex>
        <v-tooltip top>
          <v-btn
            slot="activator"
            flat icon
            color="secondary"
            :disabled="!results.length"
            @click="downloadCsv"
          >
            <v-icon>far fa-download</v-icon>
          </v-btn>
          <span>Download .csv</span>
        </v-tooltip>
      </v-layout>
    </v-card-text>
    <v-data-table
      :headers="headers"
      :items="paginRes"
      class="elevation-1"
      :pagination.sync="pagin"
      :total-items="results.length"
      must-sort
      :rows-per-page-items="[5,10,25,50,100,{text:'All',value:-1}]"
    >
      <v-progress-linear slot="progress" indeterminate />
      <template slot="items" slot-scope="props">
        <td
          v-for="head in headers"
          :key="head.value"
          :class="head.cap ? 'text-capitalize' : ''"
        >
          <span
            v-for="(value, i) in castArray(get(props.item, head.value))"
            :key="i"
            :style="{ color: get(value, head.color) }"
          >
            {{
              flatMap([value], head.label)[0]
            }}{{
              castArray(get(props.item, head.value)).length - 1 - i ? ', ' : ''
            }}
          </span>
        </td>
      </template>
      <template slot="no-data">
        No Data found.
      </template>
    </v-data-table>
  </div>
</template>

<script>
/* eslint-disable object-curly-newline */
import { mapState } from 'vuex';
import moment from 'moment';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import isEqual from 'lodash/isEqual';
import castArray from 'lodash/castArray';
import sortBy from 'lodash/sortBy';
import groups from '@/lib/groups';

const perLabel = v => `${(v * 100).toFixed(2)}%`;

export default {
  props: {
    results: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      get,
      flatMap,
      castArray,
      fields: [
        { text: 'Group Name', value: 'groupHead' },
        { text: 'Duration (min)', value: 'durations', label: v => (v === -1 ? 'none' : v) },
        { text: 'Flag', value: 'flags', label: 'name', color: 'colour' },
        { text: 'Grade', value: 'grades', label: perLabel },
        { text: 'Location', value: 'locations', cap: true },
        { text: 'Patient', value: 'patients' },
        { text: 'Procedure', value: 'procedures', cap: true },
        { text: 'Step', value: 'steps', cap: true },
        { text: 'Student', value: 'students' },
        { text: 'Supervisor', value: 'supervisors' },
        { text: 'Time', value: 'time', label: v => moment(v).format('DD/MM/YYYY HH:mm') },
        { text: 'Tooth', value: 'teeth', label: v => (v === -1 ? 'none' : v) },
        { text: 'Unit', value: 'units' },
        { text: 'Count', value: 'weight.count' },
        { text: 'Count (No Duplicates)', value: 'weight.countNoDupe' },
        { text: 'Weighted Mean Grade', value: 'weight.mean', label: perLabel },
        { text: 'Weighted Mode Grade', value: 'weight.mode', label: perLabel },
        { text: 'Weighted Median Grade', value: 'weight.median', label: perLabel },
        { text: 'Weighted Maximum Grade', value: 'weight.max', label: perLabel },
        { text: 'Weighted Minimum Grade', value: 'weight.min', label: perLabel },
        { text: 'Weighted Pass Rate', value: 'weight.passRate', label: perLabel },
      ],
      heads: castArray(this.$route.query.h || ['groupHead', 'weight.mean']),
      pagin: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 0,
      },
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    perm() { return this.user.perm; },
    group() { return groups.find(g => g.value === (this.$route.query.g || 'unitId')); },
    groupHead() {
      const head = this.group.label.replace(/\.0.*$/, '');
      return head === 'label' ? 'time' : head;
    },
    headers() {
      let headers = this.fields.filter(f => this.heads.indexOf(f.value) !== -1);
      if (headers[0].value === 'groupHead') {
        headers = headers.filter(h => h.value !== this.groupHead);
        let newFields = this.fields.filter(f => f.value === this.groupHead);
        if (this.groupHead === 'students') {
          newFields = [
            { text: 'Last Name', value: 'students_lastname' },
            { text: 'First Name', value: 'students_firstname' },
            { text: 'Pheme', value: 'students_username' },
          ];
        }
        headers.splice(0, 1, ...newFields);
        if (!headers.find(h => h.value === this.pagin.sortBy)) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.pagin.sortBy = newFields[0].value;
        }
      }
      return headers;
    },
    paginRes() {
      let sorted = sortBy(this.results, this.pagin.sortBy);
      if (this.pagin.descending) sorted = sorted.reverse();
      if (this.pagin.rowsPerPage === -1) return sorted;
      return sorted.splice((this.pagin.page - 1) * this.pagin.rowsPerPage, this.pagin.rowsPerPage);
    },
  },
  methods: {
    csv() {
      let sorted = sortBy(this.results, this.pagin.sortBy);
      if (this.pagin.descending) sorted = sorted.reverse();
      let csv = `${this.headers.map(h => `"${h.text}"`).join(',')}\n`;
      sorted.forEach((r) => {
        csv += `${
          this.headers.map(h => `"${flatMap(castArray(get(r, h.value)), h.label).join(', ')}"`).join(',')
        }\n`;
      });
      return csv;
    },
    downloadCsv() {
      const link = document.createElement('a');
      link.href = `data:text/csv;charset=utf-8,${this.csv()}`;
      link.setAttribute('download', `Coeus_Report_${moment().format('HH-mm_DD-MM-YYYY')}.csv`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
    },
  },
  watch: {
    heads(v) {
      const h = isEqual(v, ['groupHead', 'weight.mean']) ? undefined : v;
      this.$router.push({ query: { ...this.$route.query, h } });
    },
  },
};
</script>
